import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getAvatarUrl, DEFAULT_AVATAR_ID } from '../config/avatars';
import { SidebarInner, BrandMark } from './dashboard/AppSidebar';
import {
  LayoutDashboard, Users, Briefcase, Building2, BarChart3, Menu, X,
  CheckSquare, FolderOpen, Layers, Workflow, Handshake,
} from 'lucide-react';

const NAV_ITEMS = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/candidates', label: 'Candidates', icon: Users },
  { path: '/positions', label: 'Jobs', icon: Briefcase },
  { path: '/pipelines', label: 'Pipelines', icon: Workflow },
  { path: '/agencies', label: 'Agencies', icon: Handshake },
  { path: '/tasks', label: 'Tasks', icon: CheckSquare },
  { path: '/documents', label: 'Documents', icon: FolderOpen },
  { path: '/job-boards', label: 'Job Boards', icon: Layers },
  { path: '/my-company', label: 'Company', icon: Building2 },
  { path: '/company-analytics', label: 'Analytics', icon: BarChart3 },
];

const readCollapsed = () => {
  try { return localStorage.getItem('iq_sidebar_collapsed') === '1'; } catch { return false; }
};

export const RecruiterLayout = ({ children, title, subtitle, headerRight, tabs }) => {
  const { userProfile, signOut } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);

  const toggleCollapse = () => setCollapsed((c) => {
    const next = !c;
    try { localStorage.setItem('iq_sidebar_collapsed', next ? '1' : '0'); } catch { /* noop */ }
    return next;
  });

  const handleLogout = () => { signOut(); navigate('/login'); };

  const displayName = userProfile?.firstName && userProfile?.lastName
    ? `${userProfile.firstName} ${userProfile.lastName}`
    : userProfile?.email || '';
  const avatarUrl = getAvatarUrl(userProfile?.avatarUrl || DEFAULT_AVATAR_ID);
  const roleLabel = userProfile?.role === 'client_admin' ? 'Recruiter Admin' : 'Recruiter';
  const tenantBadge = userProfile?.organizationName || 'Recruiter Portal';

  const sidebarProps = {
    navItems: NAV_ITEMS,
    brand: { homePath: '/dashboard', tagline: tenantBadge },
    user: { displayName, roleLabel, avatarUrl },
    onLogout: handleLogout,
    onProfile: () => { setMobileOpen(false); navigate('/user-profile'); },
    ids: {
      brand: 'recruiter-brand',
      tagline: 'recruiter-tenant-badge',
      nav: 'recruiter-nav',
      navPrefix: 'recruiter-nav',
      profile: 'recruiter-menu-profile',
      logout: 'recruiter-menu-logout',
      collapse: 'recruiter-collapse-toggle',
    },
  };

  return (
    <div className="min-h-screen app-shell">
      {/* Desktop sidebar */}
      <aside
        className={`hidden lg:flex lg:flex-col fixed inset-y-0 left-0 z-40 transition-all duration-300 ${collapsed ? 'w-20' : 'w-64'}`}
        data-testid="recruiter-sidebar"
      >
        <SidebarInner {...sidebarProps} collapsed={collapsed} onToggleCollapse={toggleCollapse} />
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-40 bg-white border-b border-slate-200 flex items-center justify-between px-4 h-14">
        <Link to="/dashboard" className="flex items-center gap-2.5">
          <BrandMark size={30} />
          <h1 className="text-lg font-display font-bold text-slate-900 notranslate" translate="no">Jobasti<span className="text-blue-600">IQ</span></h1>
        </Link>
        <button onClick={() => setMobileOpen(true)} className="text-slate-700" data-testid="recruiter-mobile-menu-btn">
          <Menu className="w-5 h-5" />
        </button>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50" data-testid="recruiter-mobile-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-64 flex flex-col shadow-xl">
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute top-4 right-3 z-10 text-slate-400 hover:text-slate-700"
              data-testid="recruiter-mobile-close-btn"
            >
              <X className="w-5 h-5" />
            </button>
            <SidebarInner {...sidebarProps} onNavigate={() => setMobileOpen(false)} />
          </div>
        </div>
      )}

      {/* Main */}
      <div className={`${collapsed ? 'lg:pl-20' : 'lg:pl-64'} flex flex-col min-h-screen transition-all duration-300`}>
        {(title || tabs) && (
          <header className="bg-white/85 backdrop-blur border-b border-slate-200 sticky top-0 z-20 px-6 lg:px-8 pt-6 pb-0">
            <div className="flex items-start justify-between gap-4 pb-4">
              <div className="min-w-0">
                {title && <h1 className="text-2xl sm:text-3xl font-display font-semibold tracking-tight text-slate-900" data-testid="recruiter-page-title">{title}</h1>}
                {subtitle && <p className="text-sm font-medium text-slate-500 mt-1">{subtitle}</p>}
              </div>
              {headerRight && <div className="shrink-0">{headerRight}</div>}
            </div>
            {tabs && (
              <div className="flex items-center gap-8 border-b border-slate-200 -mb-px overflow-x-auto">
                {tabs.map((tab) => (
                  <button
                    key={tab.label}
                    onClick={tab.onClick}
                    className={`pb-3.5 text-sm font-medium whitespace-nowrap transition-colors relative ${
                      tab.active
                        ? 'text-blue-600 font-semibold after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-blue-600'
                        : 'text-slate-500 hover:text-slate-900'
                    }`}
                    data-testid={`recruiter-tab-${tab.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}
          </header>
        )}
        <main className="flex-1">{children}</main>
      </div>
    </div>
  );
};

export default RecruiterLayout;
